const ApiResolvers = require('@/lib/Api/ApiResolvers');
const OrderAdjustmentResponse = require('./OrderAdjustmentResponse');
const { createFieldGroupCollectionFromOrder } = require('@/lib/CustomFields/fieldGroupCollectionFactory');
const { hydrate } = require('@/lib/Hydration');

/**
 * Handles sending the API request to create an order adjustment from a cart.
 *
 * @constructor
 *
 * @param {ApiClient} apiClient
 * @param {CartToConfirmationStructureMapper} cartToConfirmationStructureMapper
 * @param {OrderRelationAttacher} orderRelationAttacher
 */
const OrderAdjustmentConfirmer = function (
    apiClient,
    cartToConfirmationStructureMapper,
    orderRelationAttacher
) {
    this.apiClient = apiClient;

    /**
     * @private
     * @type {CartToConfirmationStructureMapper}
     */
    this.cartToConfirmationStructureMapper = cartToConfirmationStructureMapper;

    /**
     * @private
     * @type {OrderRelationAttacher}
     */
    this.orderRelationAttacher = orderRelationAttacher;
};

OrderAdjustmentConfirmer.prototype = {
    /**
     * Dry-run creating an adjustment order from a cart.
     *
     * @param {Cart} cart
     *
     * @returns {Promise<OrderAdjustmentResponse>}
     */
    validateAdjustment(cart) {
        const orderConfirmationStructure = this.cartToConfirmationStructureMapper.mapOrderAdjustment(cart);
        orderConfirmationStructure.dryRun = 1;

        return this.sendAdjustmentCreationRequest(orderConfirmationStructure);
    },

    /**
     * Create an adjustment order from a cart.
     * This no longer does anything with payments.
     *
     * @param {Cart} cart
     *
     * @returns {Promise<OrderAdjustmentResponse>}
     */
    confirmAdjustment(cart) {
        const orderConfirmationStructure = this.cartToConfirmationStructureMapper.mapOrderAdjustment(cart);

        return this
            .sendAdjustmentCreationRequest(orderConfirmationStructure)
            .then(async (orderAdjustmentResponse) => {
                // The adjustment order has been created.

                // Add the originalOrder to the response because it does not come back from the API.
                orderAdjustmentResponse.originalOrder = cart.originalOrder;

                // The API does not return payments attached to these orders.
                // At this point we won't have added any additional payments, so we can copy over the payments
                // we know exist so far from the original order.
                orderAdjustmentResponse.adjustedOrder.setPayments(orderAdjustmentResponse.originalOrder?.payments || []);

                // The adjustments endpoint returns different data to the orders endpoint, so we need to do some
                // fiddling here to attach things to the Order model that we expect to be there.
                // Note that the response contains 2 orders:
                // - The adjustment order, which is the delta between the original and the desired state.
                // - The adjusted order, which is the final order after the delta has been applied.

                await Promise.all([
                    orderAdjustmentResponse.adjustmentOrder
                        ? this.orderRelationAttacher.attachRelationsToOrder(orderAdjustmentResponse.adjustmentOrder)
                        : null,

                    orderAdjustmentResponse.adjustedOrder
                        ? this.orderRelationAttacher.attachRelationsToOrder(orderAdjustmentResponse.adjustedOrder)
                        : null,
                ]);

                // Attach field data to adjustmentOrder.
                // Create a FieldGroupCollection using the field data from the modified order that was sent to the API.
                if (orderAdjustmentResponse.adjustmentOrder) {
                    orderAdjustmentResponse.adjustmentOrder.fields = createFieldGroupCollectionFromOrder(cart);
                }

                // Attach payment channel to adjustmentOrder.
                if (orderAdjustmentResponse.adjustmentOrder) {
                    this.orderRelationAttacher.attachDefaultPaymentChannelToOrder(
                        orderAdjustmentResponse.adjustmentOrder
                    );
                }

                // Attach customer data to adjustmentOrder.
                // Use the customer from the original order.
                // The customer probably isn't actually set on the adjustment order anyway, only on the original order.
                if (orderAdjustmentResponse.adjustmentOrder && orderAdjustmentResponse.originalOrder) {
                    if (
                        !orderAdjustmentResponse.adjustmentOrder.customer.contact.hasData()
                        && orderAdjustmentResponse.originalOrder.customer.contact.hasData()
                    ) {
                        orderAdjustmentResponse.adjustmentOrder.customer = orderAdjustmentResponse
                            .originalOrder.customer;
                    }
                }

                return orderAdjustmentResponse;
            });
    },

    /**
     * @param {OrderAdjustmentConfirmationStructure} orderConfirmationStructure
     *
     * @returns {Promise<OrderAdjustmentResponse>}
     */
    sendAdjustmentCreationRequest(orderConfirmationStructure) {
        const queryParams = new URLSearchParams();
        queryParams.set(
            'resolve',
            [
                'seller',
                'items',
                'offers',
                'bookinggroups',
                'orderItems.optionSets',
                ApiResolvers.ORDER_FULFILLMENT_INSTRUCTIONS,
            ].join(',')
        );

        // If onlineTaskTrigger exists add it to the query params.
        if (orderConfirmationStructure.onlineTaskTrigger) {
            queryParams.set('onlineTaskTrigger', orderConfirmationStructure.onlineTaskTrigger);
        }

        return this.apiClient
            .request(
                'POST',
                `orderadjustments/${orderConfirmationStructure.adjustedOrderID}?` + queryParams.toString(),
                {
                    ...orderConfirmationStructure,
                }
            )
            .then(({ data }) => {
                if (data && data.success) {
                    return this.buildResponseModelFromResponse(data);
                }
                throw new Error(data.error || data.message || '');
            });
    },

    /**
     * @param {object} responseData
     *
     * @return {OrderAdjustmentResponse}
     */
    buildResponseModelFromResponse(responseData) {
        let response = new OrderAdjustmentResponse();

        hydrate(responseData, response);

        return response;
    },
};

module.exports = OrderAdjustmentConfirmer;
